type AddressType = "solana" | "evm";
interface AddressValidationResult {
  isValid: boolean;
  type: AddressType;
  error?: string;
}

const SOLANA_BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const SOLANA_BASE58_VALUES = new Map(
  Array.from(SOLANA_BASE58_ALPHABET, (char, index) => [char, index] as const),
);
const SOLANA_PUBLIC_KEY_BYTE_LENGTH = 32;

const decodeBase58ToBytes = (value: string): Uint8Array | null => {
  if (value.length === 0) {
    return null;
  }

  let leadingZeroes = 0;
  while (leadingZeroes < value.length && value[leadingZeroes] === "1") {
    leadingZeroes += 1;
  }

  const bytes: number[] = [];
  for (let index = leadingZeroes; index < value.length; index += 1) {
    const alphabetValue = SOLANA_BASE58_VALUES.get(value.charAt(index));
    if (alphabetValue === undefined) {
      return null;
    }

    let carry = alphabetValue;
    for (let byteIndex = 0; byteIndex < bytes.length; byteIndex += 1) {
      const byteValue = bytes[byteIndex];
      if (byteValue === undefined) {
        return null;
      }
      const nextValue = byteValue * SOLANA_BASE58_ALPHABET.length + carry;
      bytes[byteIndex] = nextValue & 0xff;
      carry = nextValue >> 8;
    }

    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }

    if (bytes.length + leadingZeroes > SOLANA_PUBLIC_KEY_BYTE_LENGTH) {
      return null;
    }
  }

  const decoded = new Uint8Array(leadingZeroes + bytes.length);
  decoded.set(bytes.reverse(), leadingZeroes);
  return decoded;
};

const isSolanaPublicKey = (address: string): boolean =>
  decodeBase58ToBytes(address)?.length === SOLANA_PUBLIC_KEY_BYTE_LENGTH;

export function validateCryptoAddress(
  address: string,
): AddressValidationResult {
  if (!address || typeof address !== "string") {
    return {
      isValid: false,
      type: "evm", // Default type for invalid addresses
      error: `Invalid address: ${address}`,
    };
  }

  // Try Solana validation first
  if (isSolanaPublicKey(address)) {
    return {
      isValid: true,
      type: "solana",
    };
  }

  // Ethereum address validation
  // Ethereum addresses are 42 characters long (0x + 40 hex characters)
  // Case-insensitive: accepts both 0x and 0X prefix for normalization
  const ethAddressRegexCaseInsensitive = /^0[xX][a-fA-F0-9]{40}$/;

  if (ethAddressRegexCaseInsensitive.test(address)) {
    return {
      isValid: true,
      type: "evm",
    };
  }

  return {
    isValid: false,
    type: "evm", // Default type for invalid addresses
    error: `Invalid address: ${address}`,
  };
}

export const serializeAddress = (address: string) => {
  const validatedAddress = validateCryptoAddress(address);
  if (!validatedAddress.isValid) {
    throw new Error(`Invalid address: ${address}`);
  }
  if (validatedAddress.type === "solana") {
    return address;
  }
  return address.toLowerCase();
};
